import { DollarSign } from 'lucide-react'
import { useEffect, useMemo, useState, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  BillingFormData,
  createProductSelectionKey,
  getProductsForSelection,
  WizardProduct,
} from '@/containers/logistic/purchaseOrders/wizard/NewPurchaseOrderWizard'
import { ProductManager } from '@/containers/billing/register/billingPortals/ProductManager'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useSalesCategoriesQuery } from '@/hooks/business/useSalesCategoriesQuery'
import { useInvoiceProductsByAreaQuery } from '@/hooks/billing/useInvoiceProductsByAreaQuery'
import { useDebounce } from '@/hooks/useDebounce'
import { InvoiceFullProductData } from '@/interfaces/billing'
import { SelledProductsInterface } from '@/interfaces/ServerInterfaces'

import { AreaSelector } from './components'

interface ProductSelectionStepProps {
  formData: BillingFormData
  onNext: (data: Partial<BillingFormData>) => void
  onBack?: (data: Partial<BillingFormData>) => void
}

// Action interfaces (copied from ProductsCard)
interface AddProductAction {
  type: 'ADD'
  tempId: string
  product: InvoiceFullProductData
  quantity: number
  priceUnitary: { amount: number; codeCurrency: string }
}

interface ModifyProductAction {
  type: 'MODIFY'
  originalProduct: SelledProductsInterface
  newQuantity: number
  newPrice: number
  currency: string
}

interface DeleteProductAction {
  type: 'DELETE'
  originalProduct: SelledProductsInterface
  quantity: number
}

interface ProductManagerActions {
  addActions: AddProductAction[]
  modifyActions: ModifyProductAction[]
  deleteActions: DeleteProductAction[]
}

const convertProductToSelledProduct = (
  product: WizardProduct,
  index: number
): SelledProductsInterface => {
  // Generate a consistent ID for mapping back to wizard products
  // Use a deterministic ID based on the wizard product's original ID
  let selledProductId: number

  if (product.id.startsWith('existing-')) {
    // For existing products, use the original ID
    selledProductId = parseInt(product.id.replace('existing-', ''))
  } else {
    // For new products (temp-xyz), create a deterministic ID
    // Use a hash of the temp ID to ensure consistency
    const tempIdHash = product.id.split('-')[1] || '0'
    selledProductId = parseInt(tempIdHash) || index + 100000 // Large number to avoid conflicts
  }

  return {
    id: selledProductId,
    productId: product.productId,
    name: product.name,
    quantity: product.quantity,
    type: product.type || 'STOCK',
    priceUnitary: {
      amount: product.priceUnitary.amount,
      codeCurrency: product.priceUnitary.codeCurrency,
    },
    priceTotal: {
      amount: product.totalPrice,
      codeCurrency: product.currency,
    },
    addons: [],
    areaId: '1',
    createdAt: new Date().toISOString(),
    image: {
      id: 0,
      src: '',
      thumbnail: '',
      blurHash: '',
    },
    observations: '',
    totalWeight: product.weight || 0,
    totalCubaje: product.volume || 0,
    productionAreaId: null,
    productionTicketId: 0,
    status: 'COMPLETED',
    updatedAt: new Date().toISOString(),
    variationId: null,
    totalCost: 0,
    modifiedPrice: false,
    variation: null,
    measure: 'UNIT',
    barCode: undefined,
  }
}

const convertActionsToProducts = (
  actions: ProductManagerActions,
  existingProducts: WizardProduct[]
): WizardProduct[] => {
  let resultProducts = [...existingProducts]

  // Helper function to find wizard product by comparing with the converted SelledProduct ID
  const findWizardProductBySelledProductId = (
    selledProductId: number
  ): number => {
    return resultProducts.findIndex((wizardProduct, index) => {
      // Recreate the same ID logic used in convertProductToSelledProduct
      let expectedSelledId: number

      if (wizardProduct.id.startsWith('existing-')) {
        expectedSelledId = parseInt(wizardProduct.id.replace('existing-', ''))
      } else {
        const tempIdHash = wizardProduct.id.split('-')[1] || '0'
        expectedSelledId = parseInt(tempIdHash) || index + 100000
      }

      return expectedSelledId === selledProductId
    })
  }

  // Process delete actions
  actions.deleteActions.forEach((deleteAction: DeleteProductAction) => {
    const existingIndex = findWizardProductBySelledProductId(
      deleteAction.originalProduct.id
    )

    if (existingIndex >= 0) {
      const existingProduct = resultProducts[existingIndex]
      if (deleteAction.quantity >= existingProduct.quantity) {
        // Complete deletion - remove the product
        resultProducts.splice(existingIndex, 1)
      } else {
        // Partial deletion - reduce quantity
        resultProducts[existingIndex] = {
          ...existingProduct,
          quantity: existingProduct.quantity - deleteAction.quantity,
          totalPrice:
            (existingProduct.quantity - deleteAction.quantity) *
            existingProduct.priceUnitary.amount,
        }
      }
    }
  })

  // Process modify actions
  actions.modifyActions.forEach((modifyAction: ModifyProductAction) => {
    const existingIndex = findWizardProductBySelledProductId(
      modifyAction.originalProduct.id
    )

    if (existingIndex >= 0) {
      resultProducts[existingIndex] = {
        ...resultProducts[existingIndex],
        quantity: modifyAction.newQuantity,
        priceUnitary: {
          amount: modifyAction.newPrice,
          codeCurrency: modifyAction.currency,
        },
        totalPrice: modifyAction.newQuantity * modifyAction.newPrice,
        currency: modifyAction.currency,
      }
    }
  })

  // Process add actions
  actions.addActions.forEach((addAction: AddProductAction) => {
    const newProduct: WizardProduct = {
      id: addAction.tempId,
      name: addAction.product.name,
      quantity: addAction.quantity,
      priceUnitary: addAction.priceUnitary,
      totalPrice: addAction.quantity * addAction.priceUnitary.amount,
      currency: addAction.priceUnitary.codeCurrency,
      productId: addAction.product.id,
      type: addAction.product.type,
      weight: addAction.product.netWeight,
      volume: addAction.product.cubaje,
    }
    resultProducts.push(newProduct)
  })

  return resultProducts
}

const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({
  formData,
  onNext,
  onBack,
}) => {
  // Updated to use the new structure with backward compatibility
  const [productSelections, setProductSelections] = useState<
    Record<string, WizardProduct[]>
  >(formData.productSelectionsByAreaAndPriceSystem ?? {})

  const [selectedPriceSystemId, setSelectedPriceSystemId] = useState<
    number | null
  >(formData.selectedPriceSystemId ?? null)

  const [isProductManagerOpen, setIsProductManagerOpen] = useState(false)

  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  const [selectedPointOfSale, setSelectedPointOfSale] = useState<number | null>(
    formData.selectedAreaSalesId ?? null
  )

  // Estados para el ProductManager
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  // Obtener productos del área seleccionada
  const {
    products: rawProducts,
    isLoadingProducts,
    isErrorProducts,
    isFetchingNextPage,
    canLoadMore,
    fetchNextPage,
    ensureProductLoaded,
    refetchProducts,
  } = useInvoiceProductsByAreaQuery({
    params: {
      areaId: Number(selectedPointOfSale),
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      ...(selectedCategory !== 'all' && {
        salesCategoryId: Number(selectedCategory),
      }),
      priceSystemId: selectedPriceSystemId!,
    },
    options: {
      enabled:
        !!selectedPointOfSale &&
        Number(selectedPointOfSale) > 0 &&
        !!selectedPriceSystemId &&
        isProductManagerOpen &&
        !!businessData?.id,
    },
    businessId: businessData?.id ?? 0,
  })

  // Obtener categorías de venta
  const { salesCategories, isLoadingSalesCategories, isErrorSalesCategories } =
    useSalesCategoriesQuery({
      params: {
        businessId: businessData?.id ?? 0,
      },
      options: {
        enabled: !!businessData?.id && isProductManagerOpen,
      },
    })

  // Funciones de stock (simplificadas para órdenes de compra)
  const getStockInfo = useCallback((product: InvoiceFullProductData) => {
    return {
      shouldShowStock: true,
      stockReal: product.totalQuantity || 0,
      stockReserved: product.totalReserved || 0,
      stockAvailable: (product.totalQuantity || 0) - (product.totalReserved || 0),
      shouldShowProduct: true,
    }
  }, [])

  const getEnhancedStockInfo = useCallback((product: InvoiceFullProductData) => {
    const baseInfo = getStockInfo(product)
    return {
      ...baseInfo,
      visualStockReal: baseInfo.stockReal,
      visualStockReserved: baseInfo.stockReserved,
      visualStockAvailable: baseInfo.stockAvailable,
    }
  }, [getStockInfo])

  const getAvailableStock = useCallback((productId: number) => {
    const product = rawProducts?.find(p => p.id === productId)
    if (!product) return 0
    return Math.max(0, (product.totalQuantity || 0) - (product.totalReserved || 0))
  }, [rawProducts])

  const formatStockNumber = useCallback((value: number, measure?: string) => {
    if (measure === 'UNIT') {
      return value.toString()
    }
    return value.toFixed(2)
  }, [])

  // Create a function to get the current form data state
  const getCurrentFormData = (): Partial<BillingFormData> => {
    return {
      productSelectionsByAreaAndPriceSystem: productSelections,
      selectedAreaSalesId: selectedPointOfSale ?? undefined,
      selectedPriceSystemId: selectedPriceSystemId ?? undefined,
    }
  }

  // Get products for current area and price system combination
  const products = useMemo(() => {
    if (
      !selectedPriceSystemId ||
      !selectedPointOfSale ||
      isLoadingBusinessData
    ) {
      return []
    }
    return getProductsForSelection(
      productSelections,
      selectedPointOfSale,
      selectedPriceSystemId
    )
  }, [
    productSelections,
    selectedPriceSystemId,
    selectedPointOfSale,
    isLoadingBusinessData,
  ])

  const totalQuantity = useMemo(() => {
    return products.reduce((acc, product) => acc + product.quantity, 0)
  }, [products])

  // Set default price system when business data loads
  useEffect(() => {
    if (!selectedPriceSystemId && !isLoadingBusinessData) {
      const defaultPriceSystem =
        businessData?.priceSystems?.find((priceSystem) => priceSystem.isMain) ??
        businessData?.priceSystems?.[0]

      if (defaultPriceSystem) {
        setSelectedPriceSystemId(defaultPriceSystem.id)
      }
    }
  }, [businessData?.priceSystems, isLoadingBusinessData, selectedPriceSystemId])

  const existingProductsForManager = useMemo(() => {
    return products.map((product, index) =>
      convertProductToSelledProduct(product, index)
    )
  }, [products])

  const handleSaveProducts = (
    actions: ProductManagerActions
  ) => {
    if (!selectedPriceSystemId || !selectedPointOfSale) return

    const updatedProducts = convertActionsToProducts(actions, products)
    const selectionKey = createProductSelectionKey(
      selectedPointOfSale,
      selectedPriceSystemId
    )

    setProductSelections((prev) => ({
      ...prev,
      [selectionKey]: updatedProducts,
    }))

    setIsProductManagerOpen(false)
  }

  const handlePriceSystemChange = (value: string) => {
    setSelectedPriceSystemId(parseInt(value))
  }

  const handlePointOfSaleChange = (value: string) => {
    setSelectedPointOfSale(parseInt(value))
  }

  const handleNextClick = () => {
    const currentData = getCurrentFormData()
    onNext(currentData)
  }

  const handleBackClick = () => {
    const currentData = getCurrentFormData()
    if (onBack) {
      onBack(currentData)
    }
  }

  const { totalWeight, totalVolume, currencyTotals } = useMemo(() => {
    const totals: {
      currencyTotals: { [key: string]: number }
      totalWeight: number
      totalVolume: number
    } = {
      currencyTotals: {},
      totalWeight: 0,
      totalVolume: 0,
    }
    products.forEach((p) => {
      if (!totals.currencyTotals[p.currency])
        totals.currencyTotals[p.currency] = 0
      totals.currencyTotals[p.currency] += p.totalPrice
      if (p.weight) totals.totalWeight += p.weight * p.quantity
      if (p.volume) totals.totalVolume += p.volume * p.quantity
    })
    return totals
  }, [products])

  return (
    <div className="space-y-6">
      <div className="mt-4 flex flex-col gap-4 md:flex-row">
        <AreaSelector
          selectedPointOfSale={selectedPointOfSale?.toString() ?? ''}
          setSelectedPointOfSale={handlePointOfSaleChange}
        />
        <div className="flex flex-col gap-2">
          <Label htmlFor="point-of-sale" className="text-lg font-medium">
            Sistema de Precios
          </Label>
          <Select
            value={selectedPriceSystemId?.toString() ?? ''}
            onValueChange={handlePriceSystemChange}
            disabled={
              !businessData?.priceSystems ||
              businessData?.priceSystems.length === 0
            }
          >
            <SelectTrigger id="price-system" className="w-full md:w-[150px]">
              <SelectValue placeholder="Seleccionar sistema de precios" />
            </SelectTrigger>
            <SelectContent>
              {businessData?.priceSystems &&
                businessData?.priceSystems.map((priceSystem) => (
                  <SelectItem
                    key={priceSystem.id}
                    value={priceSystem.id.toString()}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      {priceSystem.name}
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Products Section - Fixed Layout */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">
            Productos ({totalQuantity} elemento{totalQuantity !== 1 ? 's' : ''})
          </h3>

          <Button
            variant="outline"
            onClick={() => setIsProductManagerOpen(true)}
            disabled={!selectedPriceSystemId || !selectedPointOfSale}
          >
            {products.length > 0 ? 'Modificar Productos' : 'Añadir Productos'}
          </Button>
        </div>

        {/* Products List or Empty State */}
        <div className="relative">
          {products.length > 0 ? (
            <div className="max-h-60 space-y-3 overflow-y-auto rounded-md border p-4">
              <ul className="space-y-2">
                {products.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between border-b pb-2 text-sm last:border-b-0"
                  >
                    <div>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground"> x {p.quantity}</span>
                    </div>
                    <span className="font-medium">
                      {p.totalPrice.toFixed(2)} {p.currency}
                    </span>
                  </li>
                ))}
              </ul>
              <Separator />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between font-medium">
                  <p>Subtotal:</p>
                  <div className="flex flex-col justify-end font-medium">
                    {Object.entries(currencyTotals).map(([currency, total]) => (
                      <span key={currency}>
                        {total.toFixed(2)} {currency}
                      </span>
                    ))}
                  </div>
                </div>
                {totalWeight > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Peso Total:</span>
                    <span>{totalWeight.toFixed(2)} kg</span>
                  </div>
                )}
                {totalVolume > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Volumen Total:</span>
                    <span>{totalVolume.toFixed(3)} m³</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed p-8 text-center text-muted-foreground">
              <p>No hay productos seleccionados.</p>
              <p className="text-sm">
                Haz clic en "Añadir Productos" para añadirlos.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ProductManager - Separated from the main layout */}
      <ProductManager
        open={isProductManagerOpen}
        onOpenChange={setIsProductManagerOpen}
        existingProducts={existingProductsForManager}
        onSave={handleSaveProducts}
        documentType={formData.documentType as any}
        priceSystemId={selectedPriceSystemId!}
        isTemporaryChanges={true}
        selectedPointOfSale={selectedPointOfSale}
      />

      {/* Hidden buttons for navigation */}
      <button
        onClick={handleNextClick}
        className="hidden"
        id="product-selection-next"
      ></button>

      <button
        onClick={handleBackClick}
        className="hidden"
        id="product-selection-back"
      ></button>
    </div>
  )
}

export default ProductSelectionStep
